/**
 * Helle oder dunkle Darstellung.
 *
 * Vorgabe ist die Einstellung des Geräts. Bei Sonne ist Hell mit dunkler
 * Schrift meist besser abzulesen, in der Dämmerung blendet es — deshalb bleibt
 * die Wahl beim Fahrer und wird gemerkt.
 */

export type Theme = 'system' | 'hell' | 'dunkel';

const KEY = 'konvoi.theme';

export function currentTheme(): Theme {
  const gespeichert = localStorage.getItem(KEY);
  return gespeichert === 'hell' || gespeichert === 'dunkel' ? gespeichert : 'system';
}

export function applyTheme(theme: Theme): void {
  if (theme === 'system') {
    document.documentElement.removeAttribute('data-thema');
    localStorage.removeItem(KEY);
  } else {
    document.documentElement.setAttribute('data-thema', theme);
    localStorage.setItem(KEY, theme);
  }
  meldeGrundfarbe();
}

/**
 * Dem Gerät die Grundfarbe nennen, damit die Statusleiste dazu passt.
 *
 * Über der Karte liegt oben ein Streifen in der Grundfarbe — sonst mattiert
 * iOS die Karte unter der Statusleiste, und sie sieht dort verwaschen aus
 * (siehe `.fahrt::before` in styles.css). Nennt man dem Gerät dieselbe Farbe,
 * steht die Statusleiste nicht daneben.
 *
 * Genannt wird die tatsächlich gewählte Darstellung, nicht die des Geräts:
 * Wer bei Sonne ausdrücklich Hell wählt, will oben keinen schwarzen Balken.
 */
function meldeGrundfarbe(): void {
  const angabe = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (angabe === null) {
    return;
  }
  const grund = getComputedStyle(document.documentElement).getPropertyValue('--grund').trim();
  if (grund !== '') {
    angabe.content = grund;
  }
}

/**
 * Wechselt das Gerät zwischen Hell und Dunkel, solange „Wie das Gerät" gilt,
 * ändern sich die Farben von selbst — die genannte Grundfarbe nicht. Sie wird
 * hier nachgezogen. Rückgabe: das Abmelden des Beobachters.
 */
export function watchSystemTheme(): () => void {
  const abfrage = window.matchMedia('(prefers-color-scheme: dark)');
  const beiWechsel = (): void => {
    if (currentTheme() === 'system') {
      meldeGrundfarbe();
    }
  };
  abfrage.addEventListener('change', beiWechsel);
  return () => {
    abfrage.removeEventListener('change', beiWechsel);
  };
}

export function themeLabel(theme: Theme): string {
  switch (theme) {
    case 'hell':
      return 'Hell';
    case 'dunkel':
      return 'Dunkel';
    default:
      return 'Wie das Gerät';
  }
}

export function nextTheme(theme: Theme): Theme {
  switch (theme) {
    case 'system':
      return 'hell';
    case 'hell':
      return 'dunkel';
    default:
      return 'system';
  }
}
